import React, { useEffect, useMemo, useState } from 'react';
import './css/index.scss';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useInputState from '../../helpers/hooks/useInputState';
import PageTitle from '../../components/PageTitle';
import MyButton from '../../components/MyButton';
import SearchInput from '../../components/SearchInput';
import SelectButton from '../../components/SelectButton';
import SelectCategory from './components/SelectCategory';
import {
  getCategoriesRequest,
  getCountriesRequest,
  getLanguagesRequest,
} from '../../store/actions/staticData';
import SelectSubCategories from './components/SelectSubCategories';
import SelectCity from './components/SelectCity';
import MapView from './components/MapView';
import DaysSection, { days } from './components/DaysSection';
import ContactsSection from './components/ContactsSection';
import getNameToShowOfMany from '../../helpers/getNameToShowOfMany';
import SelectLanguages from './components/SelectLanguages';
import SelectTags from './components/SelectTags';
import MyCheckBox from '../../components/MyCheckbox';
import { WORDS_LIST } from '../../helpers/getWordForm';
import SelectPhotos from './components/SelectPhotos';
import checkAddOrganizationPayload from '../../helpers/checkAddOrganizationPayload';
import MyModal from '../../components/MyModal';
import sendRequest from '../../helpers/sendRequest';
import Api from '../../Api';
import SelectAdditionalCities from './components/SelectAdditionalCities';
import SelectTemplatePhotos from './components/SelectTemplatePhotos';
import {
  buildOrganizationAutofillCommonFileContent,
  buildOrganizationAutofillCitiesFileContent,
  fetchOrganizationAutofillCommonData,
  fetchOrganizationAutofillCitiesData,
  fetchAutofillDataForSelectedCountry,
  parseOrganizationAutofillJson,
} from '../../helpers/organizationAutofill';
import {
  buildAllCitiesSelection,
  isAllCitiesSelection,
} from '../../helpers/allCitiesOfCountry';

const defaultSchedule = days.reduce((acc, day) => ({ ...acc, [day.value]: { start: '', end: '' } }), {});
const tempComponentStates = {
  categories: 'categories',
  subCategories: 'subCategories',
  city: 'city',
  additionalCities: 'additionalCities',
  languages: 'languages',
  tags: 'tags',
  photos: 'photos',
  templatePhotos: 'templatePhotos',
};

function AddOrganization() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    categories: categoriesFromStore,
    languages: languagesFromStore,
    countries: countriesForAutofill,
  } = useSelector((store) => store.staticData);
  const [searchParams] = useSearchParams();
  const organizationId = searchParams.get('id');
  const isEditing = Boolean(organizationId);

  const [gettingLoading, setGettingLoading] = useState(isEditing);
  const [gettingError, setGettingError] = useState('');

  const [ownerId, setOwnerId] = useInputState();
  const [name, setName] = useInputState();
  const [category, setCategory] = useState(null);
  const [subCategories, setSubCategories] = useState([]);
  const [specialization, setSpecialization] = useInputState();
  const [city, setCity] = useState(null);
  const [additionalCities, setAdditionalCities] = useState([]);
  const [address, setAddress] = useInputState();
  const [coordinates, setCoordinates] = useState(null);
  const [schedule, setSchedule] = useState(defaultSchedule);
  const [contacts, setContacts] = useState({});
  const [description, setDescription] = useInputState();
  const [languages, setLanguages] = useState([]);
  const [isForAllLanguages, setIsForAllLanguages] = useState(true);
  const [tags, setTags] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [templatePhotoIds, setTemplatePhotoIds] = useState([]);

  const [tempComponentState, setTempComponentState] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalText, setModalText] = useState('');
  const [modalState, setModalState] = useState('');
  const [loading, setLoading] = useState(false);

  const [dropdownAddresses, setDropdownAddresses] = useState([]);
  const [dropdownLoading, setDropdownLoading] = useState(false);

  const [isAutofillModeModalOpen, setIsAutofillModeModalOpen] = useState(false);
  const [isAutofillModalOpen, setIsAutofillModalOpen] = useState(false);
  const [autofillJsonText, setAutofillJsonText] = useState('');
  const [autofillError, setAutofillError] = useState('');
  const [autofillDownloadCommonLoading, setAutofillDownloadCommonLoading] = useState(false);
  const [autofillDownloadCitiesLoading, setAutofillDownloadCitiesLoading] = useState(false);
  const [autofillConvertLoading, setAutofillConvertLoading] = useState(false);
  const [autofillCountryId, setAutofillCountryId] = useState('');

  const subCategoriesName = useMemo(
    () => getNameToShowOfMany({ list: subCategories }),
    [subCategories],
  );
  const languagesName = useMemo(
    () => getNameToShowOfMany({ list: languages }),
    [languages],
  );
  const tagsName = useMemo(
    () => getNameToShowOfMany({
      list: tags, field: 'self', words: WORDS_LIST.tag, firstWord: '',
    }),
    [tags],
  );

  const isOnlineWorking = useMemo(() => Boolean(category?.is_online), [category]);
  const isInAllCitiesOfCountry = useMemo(() => isAllCitiesSelection(city), [city]);

  const handleCityChange = (nextCity) => {
    setCity(nextCity);
    if (isAllCitiesSelection(nextCity)) {
      setAddress('');
      setCoordinates(null);
      setAdditionalCities([]);
    }
  };

  const autofillCountriesSorted = useMemo(() => {
    const list = countriesForAutofill || [];
    return [...list].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'ru'));
  }, [countriesForAutofill]);

  const onCancelClick = () => {
    navigate('/organizations/view');
  };

  const openAutofillModeModal = () => {
    setIsAutofillModeModalOpen(true);
  };

  const openAutofillModal = () => {
    setIsAutofillModeModalOpen(false);
    setAutofillError('');
    setAutofillCountryId((prev) => prev || city?.country_id || '');
    setIsAutofillModalOpen(true);
  };

  const openBulkAutofillScreen = () => {
    setIsAutofillModeModalOpen(false);
    navigate('/organizations/bulk-create');
  };

  const closeAutofillModal = () => {
    setIsAutofillModalOpen(false);
    setAutofillError('');
  };

  const downloadTextFile = (content, filename) => {
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const onDownloadAutofillCommonFileClick = () => {
    setAutofillError('');
    setAutofillDownloadCommonLoading(true);
    fetchOrganizationAutofillCommonData()
      .then((payload) => {
        const content = buildOrganizationAutofillCommonFileContent(payload);
        downloadTextFile(content, 'nomad-organization-autofill-common.txt');
      })
      .catch(() => setAutofillError('Не удалось загрузить справочники. Проверьте сеть и повторите.'))
      .finally(() => setAutofillDownloadCommonLoading(false));
  };

  const onDownloadAutofillCitiesFileClick = () => {
    setAutofillError('');
    if (!autofillCountryId) {
      setAutofillError('Выберите страну — второй файл содержит только города этой страны.');
      return;
    }
    setAutofillDownloadCitiesLoading(true);
    fetchOrganizationAutofillCitiesData(autofillCountryId)
      .then(({ selectedCountry, cities }) => {
        const content = buildOrganizationAutofillCitiesFileContent({ selectedCountry, cities });
        const safe = String(selectedCountry.name || 'cities').replace(/[^\w\-]+/g, '_').slice(0, 48);
        downloadTextFile(content, `nomad-organization-autofill-cities-${safe}.txt`);
      })
      .catch(() => setAutofillError('Не удалось загрузить города. Проверьте сеть и повторите.'))
      .finally(() => setAutofillDownloadCitiesLoading(false));
  };

  const onAutofillConvert = async () => {
    setAutofillError('');
    if (!autofillCountryId) {
      setAutofillError('Выберите ту же страну, для которой был скачан справочник (города).');
      return;
    }
    setAutofillConvertLoading(true);
    try {
      const staticData = await fetchAutofillDataForSelectedCountry(autofillCountryId);
      const result = parseOrganizationAutofillJson(autofillJsonText, staticData);
      if (result.error) {
        setAutofillError(result.error);
        return;
      }
      const { patches } = result;

      if (patches.ownerId !== undefined) setOwnerId(patches.ownerId);
      if (patches.name !== undefined) setName(patches.name);
      if (patches.specialization !== undefined) setSpecialization(patches.specialization);
      if (patches.address !== undefined) setAddress(patches.address);
      if (patches.description !== undefined) setDescription(patches.description);

      if (patches.category !== undefined) {
        setCategory(patches.category);
      }
      if (patches.subCategories !== undefined) {
        setTimeout(() => setSubCategories(patches.subCategories), patches.category !== undefined ? 100 : 0);
      }

      if (patches.city !== undefined) {
        handleCityChange(patches.city);
      }
      if (patches.additionalCities !== undefined) {
        setAdditionalCities(patches.additionalCities);
      }

      if (patches.coordinates !== undefined) {
        setTimeout(() => setCoordinates(patches.coordinates), 300);
      }

      if (patches.schedulePartial !== undefined) {
        setSchedule((prev) => ({ ...prev, ...patches.schedulePartial }));
      }

      if (patches.contactsPartial !== undefined) {
        setContacts((prev) => ({ ...prev, ...patches.contactsPartial }));
      }

      if (patches.languages !== undefined) setLanguages(patches.languages);
      if (patches.tags !== undefined) setTags(patches.tags);
      if (patches.templatePhotoIds !== undefined) setTemplatePhotoIds(patches.templatePhotoIds);

      setIsAutofillModalOpen(false);
      setAutofillJsonText('');
    } catch (e) {
      setAutofillError('Ошибка при загрузке справочников или применении данных.');
    } finally {
      setAutofillConvertLoading(false);
    }
  };

  const onSave = async () => {
    const convertedSchedule = Object.keys(schedule)
      .reduce((acc, day) => ({
        ...acc,
        [day]: schedule[day].type === 'hours' ? `${schedule[day].start}-${schedule[day].end}` : schedule[day].type,
      }), {});

    const { errors, formData, payload } = checkAddOrganizationPayload({
      owner_id: ownerId,
      name,
      category_id: category?._id,
      sub_category_ids: subCategories.map((i) => i._id),
      must_set_subcategories: !!category?.sub_categories?.length,
      country_id: city?.country_id,
      city_id: isInAllCitiesOfCountry ? undefined : city?._id,
      additionalCities: isInAllCitiesOfCountry ? [] : additionalCities,
      specialization,
      address,
      coordinates,
      workTime: convertedSchedule,
      contacts,
      description,
      languages,
      is_for_all_languages: isForAllLanguages,
      tags,
      photos,
      templatePhotoIds,
      withPhotos: !isEditing,
      isOnlineWorking,
      is_in_all_cities_of_country: isInAllCitiesOfCountry,
    });

    if (errors?.length) {
      setModalText(`В следующих полях есть ошибки:\n\n${errors.join('\n\n')}`);
      setIsModalVisible(true);
      setModalState('error');
      return;
    }

    if (!isEditing) {
      const data = await sendRequest({
        request: Api.createOrganization,
        payload: formData,
        warnErrorText: 'while creating organization',
        setLoading,
      });

      setIsModalVisible(true);
      if (data?.status !== 'ok') {
        const errorText = data?.message || 'Не удалось установить соединение с сервером.';
        setModalText(`Возникла ошибка:\n\n${errorText}`);
        setModalState('error');
      } else {
        setModalText(`Предложение "${name}" успешно было создано!`);
        setModalState('success');
      }
    } else {
      const data = await sendRequest({
        request: Api.editOrganization,
        payload: { payload, id: organizationId },
        warnErrorText: 'while editing organization',
        setLoading,
      });

      setIsModalVisible(true);
      if (data?.status !== 'ok') {
        const errorText = data?.message || 'Не удалось установить соединение с сервером.';
        setModalText(`Возникла ошибка:\n\n${errorText}`);
        setModalState('error');
      } else {
        setModalText(`Предложение "${name}" успешно было редактировано!`);
        setModalState('success');
      }
    }
  };

  const onCloseModal = () => {
    if (modalState === 'error') {
      setIsModalVisible(false);
      setTimeout(() => {
        setModalText('');
        setModalState('');
      }, 400);
    } else if (modalState === 'success') {
      setIsModalVisible(false);
      navigate('/organizations/view');
    }
  };

  const getCategoriesAndSubCategoriesAtEditing = (selectedOrganization) => {
    let newCategory = {};
    const newSubCategories = [];

    if (selectedOrganization.category) {
      newCategory = categoriesFromStore.find(
        (i) => i._id === selectedOrganization.category._id,
      );

      if (
        newCategory.sub_categories
          && selectedOrganization.sub_categories.length
      ) {
        selectedOrganization.sub_categories.forEach((item) => {
          const tmp = newCategory.sub_categories.find((i) => i._id === item._id);
          if (tmp) {
            newSubCategories.push(tmp);
          }
        });
      }
    }
    return [newCategory, newSubCategories];
  };

  const getLanguagesAtEditing = (selectedOrganization) => {
    const newLanguages = [];

    if (selectedOrganization.languages?.length) {
      selectedOrganization.languages.forEach((item) => {
        const tmp = languagesFromStore.find((i) => i._id === item._id);
        if (tmp) {
          newLanguages.push(tmp);
        }
      });
    }
    return newLanguages;
  };

  const getSchedule = (selectedOrganization) => {
    const newSchedule = {};
    delete selectedOrganization.work_time.__v;
    delete selectedOrganization.work_time._id;

    Object.keys(selectedOrganization.work_time).forEach((key) => {
      const thisDayType = selectedOrganization.work_time[key];
      if (thisDayType.length === 11 && thisDayType.includes('-')) {
        // when type === hours
        const [start, end] = thisDayType.split('-');
        newSchedule[key] = { start, end, type: 'hours' };
      } else {
        newSchedule[key] = { start: '', end: '', type: thisDayType };
      }
    });

    return newSchedule;
  };

  const initData = async () => {
    const data = await sendRequest({
      request: Api.getOrganization,
      payload: { id: organizationId },
      warnErrorText: 'while getting single organization',
      setLoading: setGettingLoading,
    });

    if (data?.status !== 'ok') {
      setGettingError(typeof data.message === 'string' ? data.message : 'Ошибка');
    }

    const selectedOrganization = data.organization;
    setOwnerId(selectedOrganization.user._id);
    setName(selectedOrganization.name);
    setSpecialization(selectedOrganization.specialization);
    setAddress(selectedOrganization.address);
    setDescription(selectedOrganization.description || '');

    delete selectedOrganization.contacts.__v;
    delete selectedOrganization.contacts._id;
    setContacts(selectedOrganization.contacts);

    const [newCategory, newSubCategories] = getCategoriesAndSubCategoriesAtEditing(selectedOrganization);
    setCategory(newCategory);
    setTimeout(() => {
      setSubCategories(newSubCategories);
    }, 100);
    if (selectedOrganization.is_in_all_cities_of_country) {
      setCity(buildAllCitiesSelection(selectedOrganization.country));
      setAdditionalCities([]);
      setCoordinates(null);
    } else {
      setCity(selectedOrganization.city
        ? { ...selectedOrganization.city, is_in_all_cities_of_country: false }
        : null);
      setAdditionalCities(selectedOrganization.additional_cities || []);
      setTimeout(() => {
        setCoordinates(selectedOrganization.coordinates);
      }, 300);
    }
    setTags(selectedOrganization.tags);

    const newLanguages = getLanguagesAtEditing(selectedOrganization);
    setLanguages(newLanguages);
    setIsForAllLanguages(
      selectedOrganization.is_for_all_languages === undefined
        ? true
        : Boolean(selectedOrganization.is_for_all_languages),
    );

    const newSchedule = getSchedule(selectedOrganization);
    setSchedule(newSchedule);
  };

  const onAddressPress = async (item) => {
    setAddress(item.address_main);
    const data = await sendRequest({
      request: Api.getAddressCoordinates,
      payload: { params: { place_id: item.place_id } },
    });

    if (data?.coordinates) {
      setCoordinates(data.coordinates);
    }
  };

  const getDropdownAddresses = async ({ signal, addressVal, cityId }) => {
    const data = await sendRequest({
      request: Api.getDropdownAddresses,
      payload: { params: { address: addressVal, city_id: cityId }, signal },
      shouldLogError: false,
      setLoading: setDropdownLoading,
    });

    if (data?.status !== 'ok') {
      return setDropdownAddresses([]);
    }

    setDropdownAddresses(data.addresses);
  };

  const onMapPress = async (cords) => {
    const data = await sendRequest({
      request: Api.getPlaceNameWithCoordinates,
      payload: { params: cords },
    });

    if (data?.place_name) {
      setAddress(data.place_name);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    if (!address?.length || !city?._id) {
      setDropdownAddresses([]);
    } else {
      getDropdownAddresses({
        signal,
        addressVal: address,
        cityId: city._id,
      }).then();
    }

    return () => controller.abort({ response: { data: 'Aborted' } });
  }, [address, city]);

  useEffect(() => {
    setSubCategories([]);
  }, [category]);

  useEffect(() => {
    dispatch(getCategoriesRequest());
    dispatch(getLanguagesRequest());
    dispatch(getCountriesRequest());
  }, []);

  useEffect(() => {
    if (isEditing && categoriesFromStore?.length && languagesFromStore?.length) {
      initData().then();
    }
  }, [categoriesFromStore, languagesFromStore]);

  return (
    <div className="add_organization">
      <PageTitle
        text={isEditing ? 'Редактировать предложение' : 'Добавить предложение'}
        RightComponent={(
          <div className="add_organization_button_group">
            <MyButton
              text="Отменить"
              onClick={onCancelClick}
              className="add_organization_button_group_cancel"
              secondary
            />
            {!gettingError && (
            <MyButton
              text="Сохранить"
              onClick={onSave}
              className="add_organization_button_group_save"
              loading={loading}
            />
            )}
          </div>
        )}
      />
      {!isEditing && !gettingError && (
        <MyButton
          text="Автозаполнение"
          onClick={openAutofillModeModal}
          className="add_organization_autofill_fab"
        />
      )}
      {gettingLoading
        ? <p className="add_organization_info_text">Загрузка</p>
        : gettingError
          ? <p className="add_organization_info_text">{gettingError}</p>
          : (
            <div className="add_organization_container">
              <SearchInput
                wrapperClassName="add_organization_container_form_input"
                placeholder="ID владельца"
                withLeftIcon={false}
                value={ownerId}
                onChange={setOwnerId}
                shouldValueConsiderAsFocus={false}
              />

              <SearchInput
                wrapperClassName="add_organization_container_form_input"
                placeholder="Название"
                withLeftIcon={false}
                value={name}
                onChange={setName}
                shouldValueConsiderAsFocus={false}
              />
              <SelectButton
                label="Категория"
                value={category?.name}
                setTempComponentState={setTempComponentState}
                componentStateToSet={tempComponentStates.categories}
                isActive={tempComponentState === tempComponentStates.categories}
              />
              {!!category?.sub_categories?.length && (
              <SelectButton
                label="Подкатегории"
                value={subCategoriesName}
                setTempComponentState={setTempComponentState}
                componentStateToSet={tempComponentStates.subCategories}
                isActive={tempComponentState === tempComponentStates.subCategories}
              />
              )}
              <SearchInput
                wrapperClassName="add_organization_container_form_input"
                placeholder="Специализация"
                withLeftIcon={false}
                value={specialization}
                onChange={setSpecialization}
                shouldValueConsiderAsFocus={false}
              />
              {!isOnlineWorking && (
                <>
                  <SelectButton
                    label="Город"
                    value={city?.name}
                    setTempComponentState={setTempComponentState}
                    componentStateToSet={tempComponentStates.city}
                    isActive={tempComponentState === tempComponentStates.city}
                  />
                  {isInAllCitiesOfCountry ? (
                    <SearchInput
                      wrapperClassName="add_organization_container_form_input"
                      placeholder="Адрес"
                      withLeftIcon={false}
                      withCloseIcon={false}
                      value={city?.country?.name || ''}
                      onChange={() => {}}
                      shouldValueConsiderAsFocus={false}
                      disabled
                    />
                  ) : city?._id ? (
                    <SearchInput
                      wrapperClassName="add_organization_container_form_input"
                      placeholder="Адрес"
                      withLeftIcon={false}
                      value={address}
                      onChange={setAddress}
                      shouldValueConsiderAsFocus={false}
                      withDropdown
                      dropdownItems={dropdownAddresses}
                      dropdownLabelField="address_full"
                      dropdownKeyField="place_id"
                      onDropdownItemPress={onAddressPress}
                      dropdownLoading={dropdownLoading}
                    />
                  ) : null}
                  {!isInAllCitiesOfCountry && (
                    <MapView
                      city={city}
                      coordinates={coordinates}
                      setCoordinates={setCoordinates}
                      onMapPress={onMapPress}
                    />
                  )}
                  {!isInAllCitiesOfCountry && city?._id ? (
                    <SelectButton
                      label="Дополнительные города (необязательно)"
                      value={additionalCities.length ? `Выбрано ${additionalCities.length}` : ''}
                      setTempComponentState={setTempComponentState}
                      componentStateToSet={tempComponentStates.additionalCities}
                      isActive={tempComponentState === tempComponentStates.additionalCities}
                    />
                  ) : null}
                </>
              )}
              <DaysSection
                setSchedule={setSchedule}
                schedule={schedule}
              />
              <ContactsSection
                contacts={contacts}
                setContacts={setContacts}
              />
              <SearchInput
                wrapperClassName="add_organization_container_descr"
                withLeftIcon={false}
                placeholder="Описание"
                value={description}
                onChange={setDescription}
                isLarge
                shouldValueConsiderAsFocus={false}
              />
              <MyCheckBox
                value={isForAllLanguages}
                setValue={setIsForAllLanguages}
                text="Для всех языков (не фильтровать по языку карточки)"
                wrapperClassName="add_organization_container_checkbox"
              />
              <SelectButton
                label={isForAllLanguages ? 'Языки (необязательно)' : 'Языки'}
                value={isForAllLanguages && !languages.length ? 'Все языки' : languagesName}
                setTempComponentState={setTempComponentState}
                componentStateToSet={tempComponentStates.languages}
                isActive={tempComponentState === tempComponentStates.languages}
              />
              <SelectButton
                label="Тэги"
                value={tagsName}
                setTempComponentState={setTempComponentState}
                componentStateToSet={tempComponentStates.tags}
                isActive={tempComponentState === tempComponentStates.tags}
              />
              {!isEditing && (
                <SelectButton
                  label="Добавить фото"
                  value={photos.length ? `${photos.length} фото` : ''}
                  setTempComponentState={setTempComponentState}
                  componentStateToSet={tempComponentStates.photos}
                  isActive={tempComponentState === tempComponentStates.photos}
                />
              )}
              <SelectButton
                label="Добавить шаблонные фото"
                value={templatePhotoIds.length ? `${templatePhotoIds.length} фото` : ''}
                setTempComponentState={setTempComponentState}
                componentStateToSet={tempComponentStates.templatePhotos}
                isActive={tempComponentState === tempComponentStates.templatePhotos}
              />
            </div>
          )}

      {tempComponentState && (
        <div className="add_organization_temp_container">
          {tempComponentState === tempComponentStates.categories ? (
            <SelectCategory
              setValue={setCategory}
              setTempComponentState={setTempComponentState}
            />
          ) : tempComponentState === tempComponentStates.subCategories ? (
            <SelectSubCategories
              setValue={setSubCategories}
              setTempComponentState={setTempComponentState}
              selectedCategory={category}
              initValue={subCategories}
            />
          ) : tempComponentState === tempComponentStates.city ? (
            <SelectCity
              setValue={handleCityChange}
              setTempComponentState={setTempComponentState}
            />
          ) : tempComponentState === tempComponentStates.additionalCities ? (
            <SelectAdditionalCities
              setValue={setAdditionalCities}
              setTempComponentState={setTempComponentState}
              countryId={city?.country_id}
              initValue={additionalCities}
            />
          ) : tempComponentState === tempComponentStates.languages ? (
            <SelectLanguages
              setValue={setLanguages}
              setTempComponentState={setTempComponentState}
              initValue={languages}
            />
          ) : tempComponentState === tempComponentStates.tags ? (
            <SelectTags
              setValue={setTags}
              setTempComponentState={setTempComponentState}
              initValue={tags}
            />
          ) : tempComponentState === tempComponentStates.photos ? (
            <SelectPhotos
              setValue={setPhotos}
              setTempComponentState={setTempComponentState}
              initValue={photos}
            />
          ) : tempComponentState === tempComponentStates.templatePhotos ? (
            <SelectTemplatePhotos
              setValue={setTemplatePhotoIds}
              setTempComponentState={setTempComponentState}
              initValue={templatePhotoIds}
            />
          ) : null}
        </div>
      )}

      <MyModal isOpen={isModalVisible} closeFunction={onCloseModal}>
        <div className="add_organization_modal">
          <pre>{modalText}</pre>

          <MyButton
            className="add_organization_modal_button"
            text="Ок"
            onClick={onCloseModal}
          />
        </div>
      </MyModal>

      <MyModal
        isOpen={isAutofillModeModalOpen}
        closeFunction={() => setIsAutofillModeModalOpen(false)}
        style={{ width: 'min(520px, 92vw)' }}
      >
        <div className="add_organization_autofill_modal">
          <p className="add_organization_autofill_modal_title">Сколько предложений заполняем?</p>
          <p className="add_organization_autofill_modal_text">
            Одно — заполним текущую форму по ответу модели, как раньше. Несколько — откроется отдельный
            экран массового создания: там задаётся общий режим (страна, город, категория, владелец, фото),
            а из ответа модели получается список карточек, которые можно править и создавать пачкой.
          </p>
          <div className="add_organization_autofill_modal_actions">
            <MyButton
              text="Одно"
              onClick={openAutofillModal}
              secondary
              className="add_organization_autofill_modal_cancel"
            />
            <MyButton
              text="Несколько"
              onClick={openBulkAutofillScreen}
              className="add_organization_autofill_modal_submit"
            />
          </div>
        </div>
      </MyModal>

      <MyModal isOpen={isAutofillModalOpen} closeFunction={closeAutofillModal} style={{ width: 'min(560px, 92vw)' }}>
        <div className="add_organization_autofill_modal">
          <p className="add_organization_autofill_modal_title">Автозаполнение из JSON</p>
          <p className="add_organization_autofill_modal_text">
            Нужно два текстовых файла. Первый — общий (категории и языки, без городов), одинаков для всех стран.
            Второй — только города: скачивается отдельно для каждой страны. Отправьте оба файла в чат модели одним сообщением подряд, затем по инструкции пришлите текст о предложении.
            Ответ модели вставьте ниже (допускается блок ```json … ```).
          </p>
          <p className="add_organization_autofill_modal_hint">
            Если ответ ИИ не принимается системой из‑за неверной структуры или синтаксиса JSON, откройте
            {' '}
            <a
              href="https://jsonviewer.stack.hu/"
              target="_blank"
              rel="noopener noreferrer"
              className="add_organization_autofill_modal_link"
            >
              jsonviewer.stack.hu
            </a>
            , вставьте текст и нажмите «Format» — так проще увидеть ошибку и выровнять структуру; при необходимости исправьте по подсказкам и скопируйте JSON обратно в поле ниже.
          </p>
          <div className="add_organization_autofill_modal_country">
            <label className="add_organization_autofill_modal_country_label" htmlFor="autofill-org-country">
              Страна для второго файла (только города)
            </label>
            <select
              id="autofill-org-country"
              className="add_organization_autofill_modal_country_select"
              value={autofillCountryId}
              onChange={(e) => setAutofillCountryId(e.target.value)}
            >
              <option value="">— Выберите страну —</option>
              {autofillCountriesSorted.map((c) => (
                <option key={c._id} value={c._id}>{c.name}</option>
              ))}
            </select>
            <p className="add_organization_autofill_modal_country_note">
              Выберите страну перед скачиванием второго файла. Для «Преобразовать» укажите ту же страну, что во втором файле.
            </p>
          </div>
          <MyButton
            text="Скачать общий файл"
            onClick={onDownloadAutofillCommonFileClick}
            className="add_organization_autofill_modal_download"
            loading={autofillDownloadCommonLoading}
          />
          <MyButton
            text="Скачать файл городов страны"
            onClick={onDownloadAutofillCitiesFileClick}
            className="add_organization_autofill_modal_download_secondary"
            loading={autofillDownloadCitiesLoading}
            disabled={!autofillCountryId}
          />
          <textarea
            className="add_organization_autofill_modal_textarea"
            placeholder="Вставьте JSON сюда…"
            value={autofillJsonText}
            onChange={(e) => setAutofillJsonText(e.target.value)}
            rows={14}
            spellCheck={false}
          />
          {autofillError ? (
            <p className="add_organization_autofill_modal_error">{autofillError}</p>
          ) : null}
          <div className="add_organization_autofill_modal_actions">
            <MyButton
              text="Отмена"
              onClick={closeAutofillModal}
              secondary
              className="add_organization_autofill_modal_cancel"
            />
            <MyButton
              text="Преобразовать"
              onClick={onAutofillConvert}
              className="add_organization_autofill_modal_submit"
              loading={autofillConvertLoading}
              disabled={!autofillCountryId}
            />
          </div>
        </div>
      </MyModal>
    </div>

  );
}

export default AddOrganization;
